import { useState } from 'react';

import { PopoverOrigin } from '@mui/material';
import { Box, Checkbox, Menu, MoreVertIcon, BodyXS, BodySmall, StarIcon, LinkIcon, Tooltip, Status, Grid, Popover } from '@parspec/pixel';

import { useCustomerTabContext } from './CustomerTabContext';
import { ICustomerTabContent, ORIENTATION_TYPE } from './types';
import { ColorCode, DOCUMENT_STATUS_COLOR_MAP, LINKED_START_ICON_MESSAGE, PRIMARY_START_ICON_MESSAGE } from '../constants';
import { BomDetailObj } from './types';
import { dollarFormatter } from 'src/features/Dashboard/ProjectsTable/utils';


interface IgetMenuOptions {
    onDelete: (bomId: string, isPrimary?: boolean, bomLength?: number) => void;
    onEdit: (bomId: string) => void;
    onDuplicate: (bomId: string) => void;
    handlePopoverClose: () => void
    bomId: string;
    isPrimary?: boolean;
    bomLength: number;
}

const POPOVER_ANCHOR_ORIGIN: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
}

const POPOVER_TRANSFORM_ORIGIN: PopoverOrigin = {
    vertical: 'top',
    horizontal: 'left',
}

const TOOL_TIP_MESSAGE = `To delete this BOM, you must first assign another BOM as Primary. Open Manage BOMs to reassign.`;

const getMenuOptions = ({ onDelete, onDuplicate, bomId, isPrimary, bomLength, onEdit, handlePopoverClose }: IgetMenuOptions) => [
    { label: 'Edit', onClick: () => { onEdit(bomId); handlePopoverClose() } },
    { label: 'Duplicate', onClick: () => { onDuplicate(bomId); handlePopoverClose(); } },
    {
        label: <BodySmall sx={isPrimary && bomLength !== 1 ? { opacity: '0.4', cursor: 'default', color: 'error' } : { color: 'error' }}>Delete</BodySmall>,
        onClick: () => { onDelete(bomId, isPrimary, bomLength); handlePopoverClose() },
        color: 'error.main',
        toolTipMessage: isPrimary && bomLength !== 1 ? TOOL_TIP_MESSAGE : ''
    }
];

const CustomerTabLabelDetails: React.FC<{ anchorEl: HTMLElement | null, onClose: () => void, bomName: string; detailObj?: BomDetailObj }> = ({ anchorEl, onClose, bomName, detailObj }) => {
    const { primaryCustomerName, outsideSales, grandTotalCents, quoteStatus } = detailObj || {}

    const renderDetailRow = (label: string, value: string | React.ReactNode) => {
        return (
            <Grid container alignItems='start'>
                <Grid item xs={5} ><BodyXS color='secondary.main' sx={{ lineHeight: '16px' }}>{label}:</BodyXS></Grid>
                <Grid item xs={7}>{typeof value === 'string' ? <BodyXS color='secondary.main' sx={{ lineHeight: '16px' }} limit={false} fontWeight={500}>{value}</BodyXS> : value}</Grid>
            </Grid>
        )
    }

    const quoteStatusEl = (): React.ReactElement => {
        const color = DOCUMENT_STATUS_COLOR_MAP[quoteStatus as keyof typeof DOCUMENT_STATUS_COLOR_MAP] as ColorCode;

        return (
            <Status color={color} >
                <BodyXS color={color}>{quoteStatus}</BodyXS>
            </Status>
        )
    }

    const popoverHeadingStyle = { wordWrap: 'break-word' }
    const popoverStyle = { pointerEvents: 'none', marginTop: '6px', marginLeft: '-12px' }

    return (
        <Popover
            id="mouse-over-popover"
            sx={popoverStyle}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            anchorOrigin={POPOVER_ANCHOR_ORIGIN}
            transformOrigin={POPOVER_TRANSFORM_ORIGIN}
            onClose={onClose}
            disableRestoreFocus
        >
            <Box width={321}>
                <Box padding={2} bgcolor='secondary.main' ><BodySmall fontWeight={500} sx={popoverHeadingStyle} color='secondary.contrastText' limit={false} lines={5}>{bomName}</BodySmall></Box>
                <Box padding={2} display='flex' flexDirection='column' gap={2}>
                    {renderDetailRow('Primary Customer', primaryCustomerName || '')}
                    {renderDetailRow('Outside Sales', outsideSales || '')}
                    {renderDetailRow('Grand Total', dollarFormatter(grandTotalCents) || '')}
                    {renderDetailRow('Quote Status', quoteStatusEl())}
                </Box>
            </Box>
        </Popover>

    )

}

export const CustomerTabLabel: React.FC<ICustomerTabContent> = ({ labelId, bomName, primaryCustomerName, isPrimary, isLinked, bomLength, detailObj, showDetails = false }) => {

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const { showCheckbox, showMenu, checkBoxState, updateCheckBoxSelection, onDelete, onDuplicate, newBomIds, onEdit, orientation } = useCustomerTabContext();
    const isChecked = checkBoxState?.[labelId] || false;

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const bomNameStyle = { lineHeight: '16px' }
    const customerNameStyle = { lineHeight: '20px' }

    return (
        <>
            <Box width={1} display="flex" justifyContent="space-between" maxWidth='200px'>
                <Box display="flex" >
                    {showCheckbox && (
                        <Box
                            ml={2}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            onChange={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <Checkbox size="medium" sx={{ margin: 0, padding: 0, paddingTop: 2 }} checked={isChecked} label="" onChange={(e) => updateCheckBoxSelection(e, labelId)} />
                        </Box>
                    )}

                    <Box display="flex" pr={showCheckbox ? 1 : 0} flexDirection="column" gap="2px" alignItems="flex-start" minWidth={136} maxWidth={160} >
                        <Box display="flex" gap="6px">
                            {isPrimary && (
                                <Tooltip title={PRIMARY_START_ICON_MESSAGE}>
                                    <StarIcon fontSize="small" color="inherit" />
                                </Tooltip>
                            )}
                            {isLinked && (
                                <Tooltip title={LINKED_START_ICON_MESSAGE}>
                                    <LinkIcon fontSize="small" color="inherit" />
                                </Tooltip>
                            )}
                            <BodyXS limit disableTooltip={orientation === ORIENTATION_TYPE.HORIZONTAL} color="inherit" mt="2px" sx={bomNameStyle}>
                                {bomName}
                            </BodyXS>
                        </Box>
                        <Box display='flex' justifyContent='flex-start' width='100%' onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose} onClick={handlePopoverClose}>
                            <BodySmall color="inherit" limit disableTooltip={orientation === ORIENTATION_TYPE.HORIZONTAL} fontWeight={500} sx={customerNameStyle} >
                                {primaryCustomerName}
                            </BodySmall>
                        </Box>
                    </Box>
                </Box>

                <Box display="flex" >
                    {showMenu && (
                        <Box
                            width='100%'
                            pt={2}
                            ml={2}
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePopoverClose()
                            }}
                        >
                            <Menu options={getMenuOptions({ onDelete, onDuplicate, bomId: labelId, isPrimary, bomLength, onEdit, handlePopoverClose })}>
                                <MoreVertIcon />
                            </Menu>
                        </Box>
                    )}

                    {newBomIds?.has(Number(labelId)) && (
                        <Box width="52px" ml="6px" bgcolor="primary.main" p={1} alignSelf="center" borderRadius="6px">
                            <BodySmall color="white">NEW</BodySmall>
                        </Box>
                    )}
                </Box>
            </Box>
            {showDetails && <CustomerTabLabelDetails anchorEl={anchorEl} onClose={handlePopoverClose} bomName={bomName} detailObj={detailObj} />}
        </>
    );
};
